
import {
    Tour, Destination, Accommodation, Activity, Country,
    PartnerApiKey, BlogPost, ContentPage, MediaFolder, MediaFile,
    Customer, Booking, Payment, PaymentGateway, TourDeparture,
    SeoMetaRecord, SchemaMarkup, Passenger, TripDocument,
    CookiePolicy, PricingRule, PricingSeason,
    Language, BlogTag, Menu, Role, Author, BlogCategory, GmbLocation, LocalCitation, SiteSettings, Theme, Currency, GmbAccount,
    Room, ThemeSettings, TypographyStyle, ThemeSection, ContactOffice, Review, AiSettings, ScrapingJob, TrainingDocument, TourCategory,
    BlogContentCluster, ActivityCategory
} from '../types';

export const mapTourCategoryFromDb = (data: any): TourCategory => genericFromDb(data, { created_at: 'createdAt', updated_at: 'updatedAt' });
export const mapTourCategoryToDb = (cat: Partial<TourCategory>): any => genericToDb(cat, { createdAt: 'created_at', updatedAt: 'updated_at' });

// Safe ID Generator
export const generateId = () => {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export const getErrorMessage = (error: any): string => {
    if (!error) return 'Unknown error';
    
    // Handle Axios/API response errors
    if (error.response?.data) {
        const data = error.response.data;
        return data.details || data.error || data.message || String(data);
    }

    if (typeof error === 'string') return error;

    // Standard Error object
    if (error instanceof Error) return error.message;

    // Common API error patterns
    if (error.message) return error.message;
    if (error.error_description) return error.error_description;
    if (error.details) return error.details;
    if (error.statusText) return error.statusText;

    try {
        const str = JSON.stringify(error);
        if (str !== '{}' && str !== '[]' && str.length > 2) {
            return str;
        }
    } catch (e) {
        // JSON stringify failed
    }

    const stringVal = String(error);
    if (stringVal === '[object Object]') {
        return 'Unknown error object';
    }
    return stringVal;
};

export const fetchWithRetry = async <T>(operation: () => Promise<any>, maxRetries = 3, delay = 500): Promise<{ data: T | null; error: any }> => {
    let lastError: any;
    for (let i = 0; i < maxRetries; i++) {
        try {
            const result = await operation();
            if (result.error) throw result.error;
            return { data: result.data as T, error: null };
        } catch (err) {
            lastError = err;
            if (i < maxRetries - 1) await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
        }
    }
    return { data: null, error: lastError };
};

const toSnakeCase = (str: string) => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
const toCamelCase = (str: string) => str.replace(/_([a-z])/g, g => g[1].toUpperCase());

// Generic mappers
const genericFromDb = (data: any, customMapping: Record<string, string> = {}): any => {
    if (!data) return {};
    const result: any = {};
    for (const key in data) {
        const camelKey = customMapping[key] || toCamelCase(key);
        // Safely parse JSON strings for fields that might be stringified objects
        result[camelKey] = safeParse(data[key]);
    }
    return result;
};

const genericToDb = (data: any, customMapping: Record<string, string> = {}, nativeArrayFieldNames: string[] = []): any => {
    if (!data) return {};
    const mapped: any = {};
    for (const key in data) {
        const snakeKey = customMapping[key] || toSnakeCase(key);
        mapped[snakeKey] = data[key];
    }

    // Type handling for PostgreSQL column types
    // nativeArrayFieldNames: fields that are TEXT[] in the target table (kept as raw JS arrays).
    // All other object/array values are JSON-stringified for JSONB columns.
    const nativeArrayFields = new Set(nativeArrayFieldNames);

    for (const field of Object.keys(mapped)) {
        const val = mapped[field];
        if (val === undefined || val === null) continue;
        if (val === '') {
            if (nativeArrayFields.has(field)) {
                mapped[field] = null;
            }
            continue;
        }

        if (nativeArrayFields.has(field)) {
            // TEXT[] field: ensure raw JS array (not stringified)
            if (typeof val === 'string') {
                try {
                    const parsed = JSON.parse(val);
                    mapped[field] = Array.isArray(parsed) ? parsed : [val];
                } catch (e) {
                    mapped[field] = [val];
                }
            }
            // If it's already a JS array, leave it as-is for the pg driver
        } else if (typeof val === 'object' && val !== null) {
            // In api/Postgrest, objects/arrays are passed as-is for JSONB columns.
            // Stringifying them here can lead to double-encoded strings in the DB.
            mapped[field] = val;
        }
    }

    return mapped;
};

const safeParse = (val: any) => {
    if (typeof val !== 'string') return val;
    try {
        return JSON.parse(val);
    } catch (e) {
        return val;
    }
};

// --- START OF MAPPERS ---
export const mapTourFromDb = (data: any): Tour => {
    if (!data) return {} as Tour;
    const {
        tour_departures,
        tour_itinerary,
        itinerary,
        optional_activities,
        wildlife_encounter,
        best_time,
        best_time_to_visit,
        faqs,
        included,
        excluded,
        highlights,
        ...rest
    } = data;

    // Helper to ensure we always have an array
    const ensureArray = (val: any) => Array.isArray(val) ? val : [];

    return {
        ...genericFromDb(rest, {
            destination_id: 'destinationId',
            max_group_size: 'maxGroupSize',
            min_age: 'minAge',
            difficulty_level: 'difficultyLevel',
            language_code: 'languageCode'
        }),
        price: Number(data.price || rest.price) || 0,
        rating: Number(data.rating || rest.rating) || 0,
        bookings: Number(data.bookings || rest.bookings) || 0,
        departures: ensureArray(tour_departures || data.departures).map(mapDepartureFromDb),
        itinerary: ensureArray(itinerary || tour_itinerary).map(mapItineraryDayFromDb),
        optionalActivities: ensureArray(optional_activities || data.optionalActivities).map((act: any) => ({
            name: act.name || '',
            price: Number(act.price) || 0,
            description: act.description || ''
        })),
        wildlifeEncounter: ensureArray(wildlife_encounter),
        bestTimeToVisit: ensureArray(best_time_to_visit || best_time),
        faqs: ensureArray(faqs),
        included: ensureArray(included),
        excluded: ensureArray(excluded),
        highlights: ensureArray(highlights),
        applicableSeasonIds: safeParse(data.applicable_season_ids || rest.applicable_season_ids) || []
    };
};
// TEXT[] fields in the tours table (PostgreSQL native arrays)
const TOUR_NATIVE_ARRAY_FIELDS = [
    'highlights', 'included', 'excluded', 'categories', 'tags', 'languages',
    'related_tours', 'gallery', 'why_special', 'wildlife_encounter',
    'best_time_to_visit', 'clothing_accessories', 'photography_gear',
    'personal_items', 'optional_items'
];

export const mapTourToDb = (tour: Partial<Tour>): any => {
    // Exclude relational/virtual fields that shouldn't be saved to the tours table directly
    const { departures, tour_departures, optionalActivities, hasGroupDepartures, image, ...rest } = tour as any;

    return genericToDb(rest, {
        featuredImage: 'featured_image',
        startLocation: 'start_location',
        endLocation: 'end_location',
        accommodationPricing: 'accommodation_pricing',
        whySpecial: 'why_special',
        wildlifeEncounter: 'wildlife_encounter',
        bestTimeToVisit: 'best_time_to_visit',
        applicableSeasonIds: 'applicable_season_ids',
        clothingAccessories: 'clothing_accessories',
        photographyGear: 'photography_gear',
        personalItems: 'personal_items',
        optionalItems: 'optional_items',
        languageCode: 'language_code',
        pricingTiers: 'pricing_tiers',
        physicalRating: 'physical_rating',
        maxGroupSize: 'max_group_size',
        minAge: 'min_age',
        videoUrl: 'video_url',
        heroVideo: 'hero_video',
        isFeatured: 'is_featured',
        isPopular: 'is_popular',
        isSpecialOffer: 'is_special_offer',
        relatedTours: 'related_tours'
    }, TOUR_NATIVE_ARRAY_FIELDS);
};


export const mapDestinationFromDb = (data: any): Destination => {
    const { highlights, gallery, best_time, best_months, wildlife, climate_data, faqs, ...rest } = data;
    const ensureArray = (val: any) => Array.isArray(val) ? val : [];

    const base = genericFromDb(rest, {
        language_code: 'languageCode',
        country: 'country',
        country_name: 'country'
    });

    return {
        ...base,
        rating: Number(rest.rating) || 0,
        toursCount: Number(rest.tours_count) || 0,
        highlights: ensureArray(highlights),
        gallery: ensureArray(gallery),
        bestMonths: ensureArray(best_months),
        wildlife: ensureArray(wildlife),
        climateData: climate_data || {},
        bestTime: best_time || '',
        faqs: ensureArray(faqs),
        image: base.image || data.image || data.featured_image,
        country: base.country || data.country || data.country_name || 'Tanzania'
    };
};
export const mapDestinationToDb = (dest: Partial<Destination>): any => {
    // Remove aliased fields that don't exist as DB columns, then map
    const { countryName, ...clean } = dest as any;
    const mapped = genericToDb(clean, {
        toursCount: 'tours_count',
        bestTime: 'best_time',
        bestMonths: 'best_months',
        climateData: 'climate_data',
        travelRequirements: 'travel_requirements',
        videoUrl: 'video_url',
        languageCode: 'language_code'
    }, ['best_months', 'wildlife', 'highlights', 'gallery']);

    // Ensure image is preserved
    if (dest.image !== undefined) mapped.image = dest.image;

    // ensure country is preserved
    if (dest.country !== undefined) mapped.country = dest.country;
    return mapped;
};

export const mapAccommodationFromDb = (data: any): Accommodation => {
    const { accommodation_rooms, amenities, meal_plans, dietary_options, faqs, ...rest } = data;
    const ensureArray = (val: any) => Array.isArray(val) ? val : [];

    const base = genericFromDb(rest, {
        destination_id: 'destinationId',
        check_in_time: 'checkInTime',
        check_out_time: 'checkOutTime',
        price_range: 'priceRange',
        meal_plans: 'mealPlans',
        dietary_options: 'dietaryOptions',
        rooms_count: 'roomsCount',
        language_code: 'languageCode',
        location: 'location',
        location_name: 'location'
    });

    return {
        ...base,
        price: data.price !== undefined ? Number(data.price) : undefined,
        rooms: ensureArray(accommodation_rooms).map(mapRoomFromDb),
        amenities: ensureArray(amenities),
        mealPlans: ensureArray(meal_plans),
        dietaryOptions: ensureArray(dietary_options),
        faqs: ensureArray(faqs),
        gallery: ensureArray(data.gallery),
        image: base.image || data.image || data.featured_image,
        location: base.location || data.location || data.location_name
    };
};
export const mapAccommodationToDb = (acc: Partial<Accommodation>): any => {
    // Remove aliased/virtual fields that don't exist as DB columns
    const { rooms, locationName, location_name: _ln, accommodationRooms, ...rest } = acc as any;
    const mapped = genericToDb(rest, { destinationId: 'destination_id', checkInTime: 'check_in_time', checkOutTime: 'check_out_time', priceRange: 'price_range', mealPlans: 'meal_plans', dietaryOptions: 'dietary_options', roomsCount: 'rooms_count', languageCode: 'language_code' });

    // Ensure image is preserved
    if (acc.image !== undefined) mapped.image = acc.image;

    // preserve location
    if (acc.location !== undefined) mapped.location = acc.location;
    return mapped;
};

export const mapRoomFromDb = (data: any): Room => {
    const base = genericFromDb(data, { accommodation_id: 'accommodationId', bed_configuration: 'bedConfiguration' });
    return {
        ...base,
        price: Number(base.price) || 0,
        quantity: Number(base.quantity) || 0,
        capacity: Number(base.capacity) || 0
    };
};
export const mapRoomToDb = (room: any): any => genericToDb(room, { accommodationId: 'accommodation_id', bedConfiguration: 'bed_configuration' });

export const mapActivityFromDb = (data: any): Activity => {
    const { highlights, gallery, equipment_required, faqs, ...rest } = data;
    const ensureArray = (val: any) => Array.isArray(val) ? val : [];

    const base = genericFromDb(rest, {
        destination_id: 'destinationId',
        duration_minutes: 'durationMinutes',
        difficulty_level: 'difficultyLevel',
        min_age: 'minAge',
        language_code: 'languageCode',
        duration: 'duration',
        location: 'location'
    });

    return {
        ...base,
        price: Number(rest.price) || 0,
        rating: Number(rest.rating) || 0,
        durationMinutes: Number(rest.duration_minutes) || 0,
        minAge: Number(rest.min_age) || 0,
        maxGroupSize: Number(rest.max_group_size) || 0,
        minGroupSize: Number(rest.min_group_size) || 0,
        highlights: ensureArray(highlights),
        gallery: ensureArray(gallery),
        equipmentRequired: ensureArray(equipment_required),
        faqs: ensureArray(faqs),
        image: base.image || data.image || data.featured_image,
        duration: base.duration || data.duration || data.duration_hours || '4',
        location: base.location || data.location || data.location_name
    };
};
export const mapActivityToDb = (act: Partial<Activity>): any => {
    // Remove aliased/virtual fields that don't exist as DB columns
    const { locationName, difficultyLevel, ...clean } = act as any;
    const mapped = genericToDb(clean, {
        destinationId: 'destination_id',
        equipmentRequired: 'equipment_required',
        physicalRequirements: 'physical_requirements',
        ageRestriction: 'age_restriction',
        maxGroupSize: 'max_group_size',
        minGroupSize: 'min_group_size',
        seasonal_availability: 'seasonal_availability',
        languageCode: 'language_code',
        durationMinutes: 'duration_minutes'
    });

    // Ensure image is preserved
    if (act.image !== undefined) mapped.image = act.image;

    // Preserve duration if it exists in activity object
    if (act.duration !== undefined) mapped.duration = act.duration;

    // preserve location
    if (act.location !== undefined) mapped.location = act.location;
    return mapped;
};

export const mapCountryFromDb = (data: any): Country => genericFromDb(data, { 
    tours_count: 'toursCount', 
    destinations_count: 'destinationsCount', 
    language_code: 'languageCode',
    visa_requirements: 'visaRequirements',
    travel_advisory: 'travelAdvisory',
    best_time: 'bestTime'
});
export const mapCountryToDb = (country: Partial<Country>): any => genericToDb(country, { 
    toursCount: 'tours_count', 
    destinationsCount: 'destinations_count', 
    languageCode: 'language_code',
    visaRequirements: 'visa_requirements',
    travelAdvisory: 'travel_advisory',
    bestTime: 'best_time'
}, ['languages', 'gallery']);

export const mapApiKeyFromDb = (data: any): PartnerApiKey => genericFromDb(data, { key_prefix: 'keyPrefix', created_at: 'createdAt', last_used_at: 'lastUsedAt', usage_limit: 'usageLimit', rate_limit: 'rateLimit' });
export const mapApiKeyToDb = (key: Partial<PartnerApiKey>, fullHash?: string): any => ({ ...genericToDb(key, { keyPrefix: 'key_prefix', usageLimit: 'usage_limit', rateLimit: 'rate_limit' }), api_key_hash: fullHash });

export const mapCustomerFromDb = (data: any): Customer => {
    const base = genericFromDb(data, { first_name: 'firstName', last_name: 'lastName', loyalty_tier: 'loyaltyTier', total_bookings: 'totalBookings', total_spent: 'totalSpent', last_booking_date: 'lastBookingDate' });
    return {
        ...base,
        totalBookings: Number(base.totalBookings) || 0,
        totalSpent: Number(base.totalSpent) || 0
    };
};
export const mapCustomerToDb = (c: Partial<Customer>): any => genericToDb(c, { firstName: 'first_name', lastName: 'last_name', loyaltyTier: 'loyalty_tier', totalBookings: 'total_bookings', totalSpent: 'total_spent', lastBookingDate: 'last_booking_date' });

export const mapBookingFromDb = (data: any): Booking => {
    const base = genericFromDb(data, {
        customer_id: 'customerId',
        customer_name: 'customerName',
        customer_email: 'customerEmail',
        tour_id: 'tourId',
        departure_id: 'departureId',
        tour_title: 'tourTitle',
        booking_date: 'bookingDate',
        departure_date: 'departureDate',
        payment_status: 'paymentStatus',
        total_amount: 'total_amount',
        special_requirements: 'specialRequirements',
        cancellation_reason: 'cancellationReason',
        accommodation_level: 'accommodationLevel'
    });
    return {
        ...base,
        total_amount: Number(base.total_amount) || 0,
        adults: Number(base.adults) || 0,
        children: Number(base.children) || 0,
        infants: Number(base.infants) || 0
    };
};
export const mapBookingToDb = (booking: Partial<Booking>): any => genericToDb(booking, {
    // reference removed in favor of direct 'reference' match
    customerId: 'customer_id',
    customerName: 'customer_name',
    customerEmail: 'customer_email',
    tourId: 'tour_id',
    departureId: 'departure_id',
    tourTitle: 'tour_title',
    bookingDate: 'booking_date',
    departureDate: 'departure_date',
    paymentStatus: 'payment_status',
    total_amount: 'total_amount',
    specialRequirements: 'special_requirements',
    cancellationReason: 'cancellation_reason',
    accommodationLevel: 'accommodation_level'
});

export const mapPaymentFromDb = (data: any): Payment => {
    const base = genericFromDb(data, { transaction_id: 'transactionId', booking_ref: 'bookingRef', customer_name: 'customerName', customer_email: 'customerEmail' });
    return {
        ...base,
        amount: Number(base.amount) || 0
    };
};
export const mapPaymentToDb = (payment: Partial<Payment>): any => genericToDb(payment, { transactionId: 'transaction_id', bookingRef: 'booking_ref', customerName: 'customer_name', customerEmail: 'customer_email' });

export const mapLanguageFromDb = (data: any): Language => {
    const { id, ...rest } = data;
    return genericFromDb(rest, { native_name: 'nativeName', is_active: 'isActive', is_default: 'isDefault', flag_code: 'flagCode' });
};
export const mapLanguageToDb = (l: Partial<Language>): any => {
    const { isDefault, isActive, flagCode, nativeName, ...rest } = l as any;
    // Omit 'id' if it accidentally slipped in from DB fetch
    const clean: any = { ...rest };
    delete clean.id;

    return genericToDb(clean, {
        nativeName: 'native_name',
        isActive: 'is_active',
        isDefault: 'is_default',
        flagCode: 'flag_code'
    });
};

export const mapCurrencyFromDb = (data: any): Currency => {
    const base = genericFromDb(data, { is_base: 'isBase', last_updated: 'lastUpdated' });
    return {
        ...base,
        rate: Number(base.rate) || 0
    };
};
export const mapCurrencyToDb = (c: Partial<Currency>): any => {
    const mapped = genericToDb(c, { isBase: 'is_base', lastUpdated: 'last_updated' });
    if (mapped.rate !== undefined) {
        mapped.rate = Number(mapped.rate) || 0;
    }
    return mapped;
};

export const mapDepartureFromDb = (data: any): TourDeparture => genericFromDb(data, { tour_id: 'tourId', tour_title: 'tourTitle', start_date: 'startDate', end_date: 'endDate', available_seats: 'availableSeats', total_seats: 'totalSeats', bulk_rule: 'bulkRule' });
export const mapDepartureToDb = (dep: Partial<TourDeparture>): any => genericToDb(dep, { tourId: 'tour_id', tourTitle: 'tour_title', startDate: 'start_date', endDate: 'end_date', availableSeats: 'available_seats', totalSeats: 'total_seats', bulkRule: 'bulk_rule' });

export const mapSeoMetaFromDb = (data: any): SeoMetaRecord => data || {} as SeoMetaRecord;
export const mapSeoMetaToDb = (seo: Partial<SeoMetaRecord>): any => seo || {};

export const mapSchemaMarkupFromDb = (data: any): SchemaMarkup => data || {} as SchemaMarkup;
export const mapSchemaMarkupToDb = (schema: Partial<SchemaMarkup>): any => schema || {};

export const mapPassengerFromDb = (data: any): Passenger => genericFromDb(data);
export const mapPassengerToDb = (pax: Partial<Passenger>): any => genericToDb(pax);

export const mapDocumentFromDb = (data: any): TripDocument => genericFromDb(data, { file_path: 'filePath', file_url: 'fileUrl', uploaded_by: 'uploadedBy', uploaded_at: 'uploadedAt', passenger_name: 'passengerName' });
export const mapDocumentToDb = (doc: Partial<TripDocument>): any => genericToDb(doc, { filePath: 'file_path', fileUrl: 'file_url', uploadedBy: 'uploaded_by', uploadedAt: 'uploaded_at', passengerName: 'passenger_name' });

export const mapCookiePolicyFromDb = (data: any): CookiePolicy => genericFromDb(data, { is_default: 'isDefault' });
export const mapCookiePolicyToDb = (policy: Partial<CookiePolicy>): any => genericToDb(policy, { isDefault: 'is_default' });

export const mapPricingRuleFromDb = (data: any): PricingRule => {
    const base = genericFromDb(data, { adjustment_type: 'adjustmentType', adjustment_value: 'adjustmentValue', applicable_tours: 'applicableTours' });
    return {
        ...base,
        adjustmentValue: Number(base.adjustmentValue) || 0
    };
};
export const mapPricingRuleToDb = (rule: Partial<PricingRule>): any => genericToDb(rule, { adjustmentType: 'adjustment_type', adjustmentValue: 'adjustment_value', applicableTours: 'applicable_tours' });

export const mapSeasonFromDb = (data: any): PricingSeason => {
    const base = genericFromDb(data, { start_date: 'startDate', end_date: 'endDate' });
    return {
        ...base,
        adjustment: Number(base.adjustment) || 0
    };
};
export const mapSeasonToDb = (season: Partial<PricingSeason>): any => genericToDb(season, { startDate: 'start_date', endDate: 'end_date' });

export const mapBlogTagFromDb = (data: any): BlogTag => genericFromDb(data, { post_count: 'postCount' });
export const mapBlogTagToDb = (tag: Partial<BlogTag>): any => genericToDb(tag, { postCount: 'post_count' });

export const mapMenuFromDb = (data: any): Menu => genericFromDb(data);
export const mapMenuToDb = (menu: Partial<Menu>): any => genericToDb(menu);

export const mapRoleFromDb = (data: any): Role => genericFromDb(data, { is_system: 'isSystem' });
export const mapAuthorFromDb = (data: any): Author => genericFromDb(data, { social_links: 'socialLinks' });
export const mapAuthorToDb = (author: Partial<Author>): any => genericToDb(author, { socialLinks: 'social_links' });
export const mapBlogCategoryFromDb = (data: any): BlogCategory => genericFromDb(data, { parent_id: 'parentId', post_count: 'postCount' });
export const mapBlogCategoryToDb = (cat: Partial<BlogCategory>): any => genericToDb(cat, { parentId: 'parent_id', postCount: 'post_count' });

export const mapGmbLocationFromDb = (data: any): GmbLocation => genericFromDb(data);
export const mapGmbLocationToDb = (loc: Partial<GmbLocation>): any => genericToDb(loc);

export const mapLocalCitationFromDb = (data: any): LocalCitation => genericFromDb(data);
export const mapLocalCitationToDb = (cit: Partial<LocalCitation>): any => genericToDb(cit);

export const mapSiteSettingsFromDb = (data: any): SiteSettings => genericFromDb(data);
export const mapSiteSettingsToDb = (settings: Partial<SiteSettings>): any => genericToDb(settings, {
    showReviewsOnHomepage: 'show_reviews_on_homepage',
    googlePlaceId: 'google_place_id',
    tripAdvisorLocationId: 'trip_advisor_location_id',
    homepageAboutUs: 'homepage_about_us',
    pageFeaturedImages: 'page_featured_images',
    archiveTitles: 'archive_titles',
    whatsappNumber: 'whatsapp_number',
    supportEmail: 'support_email',
    supportPhone: 'support_phone',
    logoUrl: 'logo_url',
    faviconUrl: 'favicon_url',
    siteName: 'site_name',
    metaTitle: 'meta_title',
    metaDescription: 'meta_description',
    metaKeywords: 'meta_keywords',
    siteUrl: 'site_url',
    defaultCurrency: 'default_currency',
    maintenanceMode: 'maintenance_mode',
    maintenanceSettings: 'maintenance_settings',
    siteWidth: 'site_width'
});

export const mapBlogPostFromDb = (data: any): BlogPost => genericFromDb(data, { language_code: 'languageCode', published_date: 'publishedDate' });
export const mapBlogPostToDb = (post: Partial<BlogPost>): any => genericToDb(post, { languageCode: 'language_code', publishedDate: 'published_date' });

export const mapContentPageFromDb = (data: any): ContentPage => genericFromDb(data, { hero_config: 'heroConfig', show_toc: 'showToc', language_code: 'languageCode' });
export const mapContentPageToDb = (page: Partial<ContentPage>): any => genericToDb(page, { heroConfig: 'hero_config', showToc: 'show_toc', languageCode: 'language_code' });


export const mapMediaFolderFromDb = (data: any): MediaFolder => genericFromDb(data);
export const mapMediaFolderToDb = (folder: Partial<MediaFolder>): any => genericToDb(folder);

export const mapMediaFileFromDb = (data: any): MediaFile => genericFromDb(data);
export const mapMediaFileToDb = (file: Partial<MediaFile>): any => genericToDb(file);

export const mapPaymentGatewayFromDb = (data: any): PaymentGateway => {
    const base = genericFromDb(data);
    return {
        ...base,
        transactionFee: Number(base.transactionFee) || 0,
        fixedFee: Number(base.fixedFee) || 0
    };
};
export const mapPaymentGatewayToDb = (gw: Partial<PaymentGateway>): any => genericToDb(gw);

export const mapContactOfficeFromDb = (data: any): ContactOffice => genericFromDb(data, { staff_ids: 'staffIds' });
export const mapContactOfficeToDb = (office: Partial<ContactOffice>): any => {
    const { ...clean } = office as any;
    return genericToDb(clean, { staffIds: 'staff_ids' }, ['staff_ids']);
};

export const mapGmbAccountFromDb = (data: any): GmbAccount => genericFromDb(data);
export const mapGmbAccountToDb = (acc: Partial<GmbAccount>): any => genericToDb(acc);

export const mapAiSettingsFromDb = (data: any): AiSettings => {
    const base = genericFromDb(data, { 
        api_key: 'apiKey', 
        max_tokens: 'maxTokens', 
        enable_chat: 'enableChat', 
        enable_content_gen: 'enableContentGen', 
        enable_seo_analysis: 'enableSeoAnalysis', 
        default_prompt_tone: 'defaultPromptTone', 
        system_instruction: 'systemInstruction', 
        assistant_name: 'assistantName', 
        welcome_message: 'welcomeMessage',
        website_url: 'websiteUrl',
        show_powered_by: 'showPoweredBy'
    });
    return {
        ...base,
        temperature: Number(base.temperature) || 0,
        maxTokens: Number(base.maxTokens) || 0
    };
};
export const mapAiSettingsToDb = (settings: Partial<AiSettings>): any => genericToDb(settings, { 
    apiKey: 'api_key', 
    maxTokens: 'max_tokens', 
    enableChat: 'enable_chat', 
    enableContentGen: 'enable_content_gen', 
    enableSeoAnalysis: 'enable_seo_analysis', 
    defaultPromptTone: 'default_prompt_tone', 
    systemInstruction: 'system_instruction', 
    assistantName: 'assistant_name', 
    welcomeMessage: 'welcome_message',
    websiteUrl: 'website_url',
    showPoweredBy: 'show_powered_by'
});

export const mapReviewFromDb = (data: any): Review => ({
    id: data.id,
    author: data.author,
    avatar: data.avatar,
    rating: data.rating,
    date: data.date,
    source: data.source,
    content: data.content,
    status: data.status,
    response: data.response,
    tourName: data.tour_name,
    isFeatured: !!data.is_featured
});

export const mapThemeFromDb = (data: any): Theme => genericFromDb(data, {
    is_active: 'isActive',
    operator_selected: 'operatorSelected',
    selected_at: 'selectedAt',
    created_at: 'createdAt',
    updated_at: 'updatedAt'
});
export const mapThemeToDb = (theme: Partial<Theme>): any => genericToDb(theme, {
    isActive: 'is_active',
    operatorSelected: 'operator_selected',
    selectedAt: 'selected_at'
});

export const mapThemeSectionFromDb = (data: any): ThemeSection => {
    const config = data.config || {};
    return {
        id: data.id,
        themeId: data.theme_id,
        page: data.page,
        type: data.section_type || data.type, // Backwards compatibility
        isVisible: data.enabled ?? data.is_visible ?? true,
        order: data.position ?? data.order ?? 0,
        styles: {
            alignment: config.alignment || 'left',
            paddingTop: config.paddingTop ?? 80,
            paddingBottom: config.paddingBottom ?? 80,
            marginTop: config.marginTop ?? 0,
            marginBottom: config.marginBottom ?? 0,
            containerWidth: config.containerWidth || 'boxed',
            background: config.background || { type: 'color', color: 'transparent' },
            typography: config.typography
        },
        content: config
    };
};

export const mapThemeSectionToDb = (section: Partial<ThemeSection>): any => ({
    id: section.id,
    theme_id: section.themeId,
    page: section.page,
    section_type: section.type,
    position: section.order,
    enabled: section.isVisible,
    config: {
        ...section.content,
        ...section.styles // Flatten styles back into config for DB storage if that's the preferred pattern
    }
});

export const mapScrapingJobFromDb = (data: any): ScrapingJob => genericFromDb(data, { source_url: 'sourceUrl', last_run: 'lastRun', items_extracted: 'itemsExtracted', next_run: 'nextRun' });
export const mapScrapingJobToDb = (job: Partial<ScrapingJob>): any => genericToDb(job, { sourceUrl: 'source_url', lastRun: 'last_run', itemsExtracted: 'items_extracted', nextRun: 'next_run' });

export const mapTrainingDocumentFromDb = (data: any): TrainingDocument => genericFromDb(data, { upload_date: 'uploadDate', vector_count: 'vectorCount' });
export const mapTrainingDocumentToDb = (doc: Partial<TrainingDocument>): any => genericToDb(doc, { uploadDate: 'upload_date', vectorCount: 'vector_count' });

export const mapItineraryDayFromDb = (data: any): any => {
    if (!data) return {};
    const { day, day_number, title, description, activities, ...rest } = data;
    const ensureArray = (val: any) => Array.isArray(val) ? val : [];

    return {
        ...genericFromDb(rest),
        day: day || day_number,
        title: title || '',
        description: description || '',
        activities: ensureArray(activities).map((act: any) => ({
            name: act.name || '',
            description: act.description || '',
            price: Number(act.price) || 0,
            isOptional: !!act.is_optional || !!act.isOptional
        }))
    };
};

// SEO & Content Clusters Mappers
export const mapBlogContentClusterFromDb = (data: any): BlogContentCluster => genericFromDb(data, { pillar_page_title: 'pillarPageTitle', pillar_page_id: 'pillarPageId', avg_authority: 'avgAuthority', post_count: 'postCount', total_traffic: 'totalTraffic', cluster_type: 'clusterType', is_active: 'isActive' });
export const mapBlogContentClusterToDb = (cluster: Partial<BlogContentCluster>): any => {
    // Exclude calculated/virtual fields but keep ID
    const { pillarPageTitle, postCount, avgAuthority, totalTraffic, ...clean } = cluster as any;
    return genericToDb(clean, {
        pillarPageId: 'pillar_page_id',
        clusterType: 'cluster_type',
        isActive: 'is_active'
    }, ['secondary_keywords', 'target_entities']);
};

export const mapActivityCategoryFromDb = (data: any): ActivityCategory => genericFromDb(data);
export const mapActivityCategoryToDb = (category: Partial<ActivityCategory>): any => genericToDb(category);

